import { Platform } from 'react-native';

import { REMOVE_ADS_ENTITLEMENT, REMOVE_ADS_PRODUCTS, revenueCatApiKeys } from '../constants';
import { recordError } from './analytics';

// Lazy-loaded module — same pattern as app/lib/ads.ts. babel-preset-expo
// inlines Platform.OS at build time, so on web the require() is dropped
// before Metro can bundle it. The .web.ts stub is the belt-and-braces
// fallback.
type PurchasesModule = typeof import('react-native-purchases').default;
type PurchasesPackage = import('react-native-purchases').PurchasesPackage;
type CustomerInfo = import('react-native-purchases').CustomerInfo;

let Purchases: PurchasesModule | null = null;

if (Platform.OS !== 'web') {
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    Purchases = require('react-native-purchases').default;
  } catch (err) {
    if (__DEV__) console.warn('[revenuecat] module unavailable:', err);
  }
}

let initialized = false;
let initializing: Promise<void> | null = null;

// Truthy in two cases:
//   1) The `remove_ads` entitlement is active (canonical / production path).
//   2) Fallback: the user has a non-subscription transaction for one of the
//      configured `REMOVE_ADS_PRODUCTS`. Covers test-store apps and the case
//      where entitlements weren't wired up in the RC dashboard.
const hasRemoveAdsEntitlement = (info: CustomerInfo | null | undefined) => {
  if (!info) return false;
  if (info.entitlements?.active?.[REMOVE_ADS_ENTITLEMENT]) return true;
  const purchasedProducts =
    info.nonSubscriptionTransactions?.map((t) => t.productIdentifier) ?? [];
  return purchasedProducts.some((p) => REMOVE_ADS_PRODUCTS.includes(p));
};

// RevenueCat platform key prefixes. Anything else (e.g. `test_…`, which is
// for RevenueCat Web Billing / Stripe Test Mode) will NOT work on iOS or
// Android — the SDK will accept the configure() call but every purchase
// attempt fails because no native store products are linked. Warning loud
// at boot saves a long debug session.
const VALID_PREFIXES_BY_PLATFORM: Record<string, string[]> = {
  ios: ['appl_', 'sbox_'],
  android: ['goog_', 'sbox_'],
};

const validateApiKey = (apiKey: string): string | null => {
  const valid = VALID_PREFIXES_BY_PLATFORM[Platform.OS] ?? [];
  if (valid.length === 0) return null;
  if (valid.some((p) => apiKey.startsWith(p))) return null;
  const expected = valid.join(' or ');
  return `RevenueCat API key '${apiKey.slice(0, 8)}…' has an unrecognized prefix for ${Platform.OS} (expected ${expected}). Purchases will fail until you replace it with the platform-specific key from the RevenueCat dashboard → Project Settings → API keys.`;
};

// Initializes the RevenueCat SDK. No-ops on web and when the native module
// isn't available. Idempotent — in-flight calls are deduplicated.
export const initializeRevenueCat = async (): Promise<void> => {
  if (!Purchases || initialized) return;
  if (initializing) return initializing;

  const sdk = Purchases;
  const apiKey = Platform.OS === 'ios' ? revenueCatApiKeys.ios : revenueCatApiKeys.android;
  if (!apiKey) {
    if (__DEV__) console.warn('[revenuecat] no API key configured for', Platform.OS);
    return;
  }

  const warning = validateApiKey(apiKey);
  if (warning && __DEV__) {
    console.warn('[revenuecat]', warning);
  }

  initializing = (async () => {
    try {
      if (__DEV__) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const setLogLevel = (sdk as any).setLogLevel;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const LOG_LEVEL = (sdk as any).LOG_LEVEL;
        if (typeof setLogLevel === 'function' && LOG_LEVEL?.DEBUG) {
          setLogLevel(LOG_LEVEL.DEBUG);
        }
      }
      sdk.configure({ apiKey });
      initialized = true;
      if (__DEV__) console.log('[revenuecat] configured', Platform.OS, apiKey.slice(0, 8) + '…');
    } catch (err) {
      if (__DEV__) console.warn('[revenuecat] configure failed:', err);
      recordError(err instanceof Error ? err : new Error(String(err)), 'revenuecat:configure');
    } finally {
      initializing = null;
    }
  })();

  return initializing;
};

export const isRevenueCatInitialized = () => initialized;

export type OfferingsLookup =
  | { package: PurchasesPackage; reason: null }
  | { package: null; reason: string };

export const getRemoveAdsPackage = async (): Promise<PurchasesPackage | null> => {
  const result = await getRemoveAdsPackageWithReason();
  return result.package;
};

export const getRemoveAdsPackageWithReason = async (): Promise<OfferingsLookup> => {
  if (!Purchases) return { package: null, reason: 'native module unavailable' };
  if (!initialized) return { package: null, reason: 'SDK not initialized' };
  try {
    const offerings = await Purchases.getOfferings();
    if (__DEV__) {
      console.log('[revenuecat] offerings', {
        current: offerings.current?.identifier ?? null,
        all: Object.keys(offerings.all),
      });
    }
    const offering = offerings.current ?? offerings.all['default'] ?? null;
    if (!offering) {
      return {
        package: null,
        reason:
          'no offering returned by RevenueCat (configure a current offering named "default" with the lifetime product in the dashboard)',
      };
    }
    // We expose a single non-consumable. Prefer the lifetime package; fall
    // back to the first available package.
    const pkg =
      offering.lifetime ??
      offering.availablePackages.find((p) => p.packageType === 'LIFETIME') ??
      offering.availablePackages[0] ??
      null;
    if (!pkg) {
      return {
        package: null,
        reason: `offering "${offering.identifier}" has no packages`,
      };
    }
    return { package: pkg, reason: null };
  } catch (err) {
    // Transient network failures and dashboard mis-config land here. Not a
    // crash — surfaced to the user via the failure reason; we log in
    // __DEV__ for diagnosis.
    const msg = err instanceof Error ? err.message : String(err);
    if (__DEV__) console.log('[revenuecat] getOfferings failed:', err);
    return { package: null, reason: `getOfferings: ${msg}` };
  }
};

export type PurchaseOutcome = 'purchased' | 'cancelled' | 'failed';

const summarizeCustomerInfo = (info: CustomerInfo) => ({
  activeEntitlements: Object.keys(info.entitlements?.active ?? {}),
  allEntitlements: Object.keys(info.entitlements?.all ?? {}),
  activeSubscriptions: info.activeSubscriptions ?? [],
  nonSubscriptionTransactions:
    info.nonSubscriptionTransactions?.map((t) => t.productIdentifier) ?? [],
});

export const purchaseRemoveAds = async (
  pkg: PurchasesPackage
): Promise<{
  outcome: PurchaseOutcome;
  entitled: boolean;
  error?: Error;
  activeEntitlements?: string[];
  productIdentifier?: string;
}> => {
  if (!Purchases || !initialized) {
    return {
      outcome: 'failed',
      entitled: false,
      error: new Error('RevenueCat SDK not initialized'),
    };
  }
  try {
    if (__DEV__) console.log('[revenuecat] purchasePackage', pkg.identifier);
    const result = await Purchases.purchasePackage(pkg);
    const summary = summarizeCustomerInfo(result.customerInfo);
    if (__DEV__) {
      console.log('[revenuecat] purchase result', {
        productIdentifier: result.productIdentifier,
        ...summary,
      });
    }
    return {
      outcome: 'purchased',
      entitled: hasRemoveAdsEntitlement(result.customerInfo),
      activeEntitlements: summary.activeEntitlements,
      productIdentifier: result.productIdentifier,
    };
  } catch (err) {
    // RevenueCat throws on user cancellation with `userCancelled: true`. We
    // don't classify cancellation as a failure — no analytics-noise, no
    // alert.
    const cancelled = (err as { userCancelled?: boolean })?.userCancelled === true;
    if (cancelled) {
      return { outcome: 'cancelled', entitled: false };
    }
    // Operational outcomes (declined card, simulated test-store failure,
    // network blip, etc.) are surfaced to the user via the Alert in
    // SettingsModal. They are NOT crashes — keep them out of Crashlytics
    // and use console.log (not warn/error) so RN's dev overlay stays quiet.
    if (__DEV__) console.log('[revenuecat] purchase failed:', err);
    const e = err instanceof Error ? err : new Error(String(err));
    return { outcome: 'failed', entitled: false, error: e };
  }
};

export const restorePurchases = async (): Promise<{ entitled: boolean; error?: Error }> => {
  if (!Purchases || !initialized) {
    return { entitled: false, error: new Error('RevenueCat SDK not initialized') };
  }
  try {
    const info = await Purchases.restorePurchases();
    return { entitled: hasRemoveAdsEntitlement(info) };
  } catch (err) {
    // See purchaseRemoveAds: not a crash, the user sees the failure in the
    // Alert; we just log in __DEV__ for diagnosis.
    if (__DEV__) console.log('[revenuecat] restore failed:', err);
    const e = err instanceof Error ? err : new Error(String(err));
    return { entitled: false, error: e };
  }
};

export const checkEntitlement = async (): Promise<boolean> => {
  if (!Purchases || !initialized) return false;
  try {
    const info = await Purchases.getCustomerInfo();
    return hasRemoveAdsEntitlement(info);
  } catch (err) {
    if (__DEV__) console.warn('[revenuecat] getCustomerInfo failed:', err);
    return false;
  }
};

// Switches RevenueCat to a brand-new anonymous user. Use only for testing
// the purchase flow repeatedly — the previous purchase stays attached to
// the previous user in the dashboard, so it can be inspected there.
export const resetRcUserForTesting = async (): Promise<{
  entitled: boolean;
  userId: string;
} | null> => {
  if (!Purchases || !initialized) return null;
  try {
    const freshId = `test-${Date.now()}`;
    const result = await Purchases.logIn(freshId);
    const entitled = hasRemoveAdsEntitlement(result.customerInfo);
    if (__DEV__) {
      console.log('[revenuecat] switched to fresh user', {
        userId: freshId,
        created: result.created,
        entitled,
      });
    }
    return { entitled, userId: freshId };
  } catch (err) {
    if (__DEV__) console.warn('[revenuecat] logIn failed:', err);
    return null;
  }
};

export type EntitlementListener = (entitled: boolean) => void;

export const addEntitlementListener = (listener: EntitlementListener): (() => void) => {
  if (!Purchases) return () => {};
  const handler = (info: CustomerInfo) => listener(hasRemoveAdsEntitlement(info));
  Purchases.addCustomerInfoUpdateListener(handler);
  return () => {
    // SDK v10 exposes removeCustomerInfoUpdateListener; older versions don't.
    const sdk = Purchases as unknown as {
      removeCustomerInfoUpdateListener?: (h: typeof handler) => void;
    };
    sdk.removeCustomerInfoUpdateListener?.(handler);
  };
};

export default {
  initializeRevenueCat,
  isRevenueCatInitialized,
  getRemoveAdsPackage,
  getRemoveAdsPackageWithReason,
  purchaseRemoveAds,
  restorePurchases,
  checkEntitlement,
  addEntitlementListener,
  resetRcUserForTesting,
};
